import React, { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { Tour, type TourProps } from 'antd';
import { useTranslation } from 'react-i18next';

type TourStep = NonNullable<TourProps['steps']>[number];

interface TourContextValue {
  steps: TourStep[];
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  registerTour: (pageSteps: TourStep[]) => void;
  currentStep?: number;
}

// 建立 Context
export const TourContext = createContext<TourContextValue>({
  steps: [],
  isOpen: false,
  setIsOpen: () => {},
  registerTour: () => {},
});

interface TourProviderProps {
  children: ReactNode;
}

// Provider 元件
export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
  const [steps, setSteps] = useState<TourStep[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const { t } = useTranslation();

  // Focus 到目標元素內的 input/select/textarea
  const focusTargetInput = useCallback((stepIndex: number, currentSteps?: TourStep[]) => {
    const stepsToUse = currentSteps || steps;
    if (!stepsToUse[stepIndex]) return;

    // 使用 setTimeout 確保 Tour 動畫完成後再 focus
    setTimeout(() => {
      const targetFn = stepsToUse[stepIndex].target;
      if (typeof targetFn !== 'function') return;

      const targetElement = (targetFn as () => HTMLElement | null | undefined)();
      if (!targetElement) return;

      // 在目標元素內查找可 focus 的 input 元素
      const focusableElement = targetElement.querySelector(
        'input:not([type="hidden"]), select, textarea, .ant-select-selector, .ant-input-number-input'
      ) as HTMLElement | null;

      if (focusableElement) {
        // 對於 Ant Design Select，需要點擊 selector 來打開下拉
        if (focusableElement.classList.contains('ant-select-selector')) {
          focusableElement.click();
        } else {
          focusableElement.focus();
        }
      }
    }, 100);
  }, [steps]);

  // 追蹤上一次的 isOpen 狀態，只在從 false 變為 true 時才重置步驟
  const prevIsOpenRef = useRef(false);

  useEffect(() => {
    // 只在 Tour 剛打開時（從 false 變為 true）才重置到第一步並 focus
    if (isOpen && !prevIsOpenRef.current && steps.length > 0) {
      setCurrentStep(0);
      focusTargetInput(0, steps);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, steps]);

  // 處理步驟變化
  const handleStepChange = useCallback((current: number) => {
    setCurrentStep(current);
    focusTargetInput(current);
  }, [focusTargetInput]);

  // 註冊導覽步驟 (由各頁面呼叫)
  const registerTour = useCallback((pageSteps: TourStep[]) => {
    // 確保 pageSteps 是陣列
    const newSteps = Array.isArray(pageSteps) ? pageSteps : [];

    // 只有當步驟內容真的改變時才更新，避免無限迴圈
    // 這裡做一個簡單的長度檢查，更嚴謹可以用 JSON.stringify 比較
    setSteps(prev => {
      if (prev.length === newSteps.length &&
          prev.every((step, i) => step.target === newSteps[i].target && step.title === newSteps[i].title)) {
        return prev;
      }
      return newSteps;
    });
  }, []);

  return (
    <TourContext.Provider value={{ steps, isOpen, setIsOpen, registerTour, currentStep }}>
      {/* 全域 Tour 元件 */}
      <Tour
        open={isOpen}
        onClose={() => setIsOpen(false)}
        steps={steps.map((step, index) => ({
          ...step,
          prevButtonProps: { children: t('common.tour.previous', { defaultValue: 'Previous' }) as string },
          nextButtonProps: {
            children: (index === steps.length - 1
              ? t('common.tour.finish', { defaultValue: 'Finish' })
              : t('common.tour.next', { defaultValue: 'Next' })) as string
          },
        }))}
        current={currentStep}
        onChange={handleStepChange}
      />
      {children}
    </TourContext.Provider>
  );
};

// Hook 方便使用
export const useTour = (): TourContextValue => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};
